import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Busy, Field, Panel } from '@/components/kit';
import { SwitchRow } from '@/components/inputs';
import { PLANS, PRODUCT_TERMS } from '@/shared/pricing';
import { PRIVATE_KEYS, SLUG_RE, productPath, slugify } from '@/shared/products';

const { eq } = window.ApperSDK.CoreSDK;

const priceText = (v) => (Number(v) > 0 ? String(v) : '');

export function productToForm(p) {
  return {
    Name: p?.Name ?? '',
    slug_c: p?.slug_c ?? '',
    plan_key_c: p?.plan_key_c ?? '',
    tagline_c: p?.tagline_c ?? '',
    description_c: p?.description_c ?? '',
    features_c: p?.features_c ?? '',
    image_url_c: p?.image_url_c ?? '',
    price_1m_c: priceText(p?.price_1m_c),
    price_3m_c: priceText(p?.price_3m_c),
    price_6m_c: priceText(p?.price_6m_c),
    price_12m_c: priceText(p?.price_12m_c),
    price_once_c: priceText(p?.price_once_c),
    show_on_home_c: p ? !!p.show_on_home_c : true,
    active_c: p ? !!p.active_c : true,
    sort_c: p?.sort_c != null ? String(p.sort_c) : '10',
  };
}

export const blankProduct = () => productToForm(null);

async function slugTaken(slug, ownId) {
  const r = await sdk.table('products_c').select(['Name', 'slug_c']).where(eq('slug_c', slug)).fetch();
  const found = r?.data ?? [];
  return found.some((row) => row.Id !== ownId);
}

/** Full product form for the New and Edit pages. `onSubmit` receives the write payload. */
export default function ProductForm({
  initial,
  recordId = null,
  onSubmit,
  saving,
  submitLabel = 'Save product',
  busyLabel = 'Saving…',
  cancelTo,
}) {
  const [form, setForm] = useState(initial);
  const [slugTouched, setSlugTouched] = useState(!!initial.slug_c);
  const [checking, setChecking] = useState(false);
  const builtIn = !!form.plan_key_c;
  const privatePlan = PRIVATE_KEYS.includes(form.plan_key_c);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  function changeName(e) {
    const value = e.target.value;
    setForm((f) => ({ ...f, Name: value, slug_c: slugTouched ? f.slug_c : slugify(value) }));
  }

  function changeSlug(e) {
    setSlugTouched(true);
    set('slug_c')(e.target.value.toLowerCase().replace(/\s+/g, '-'));
  }

  async function submit(e) {
    e.preventDefault();
    const slug = form.slug_c.trim();
    if (!form.Name.trim()) return toast.error('Give the product a name');
    if (!slug) return toast.error('Add a page address');
    if (!SLUG_RE.test(slug)) return toast.error('Page address: lowercase letters, numbers and dashes only');
    const prices = Object.fromEntries(PRODUCT_TERMS.map((t) => [t.field, Math.max(0, Number(form[t.field] || 0))]));
    if (!builtIn && !Object.values(prices).some((v) => v > 0)) {
      return toast.error('Set a price for at least one term, or a one-time price');
    }
    setChecking(true);
    try {
      if (await slugTaken(slug, recordId)) return toast.error(`Another product already uses /p/${slug}`);
    } finally {
      setChecking(false);
    }
    return onSubmit({
      Name: form.Name.trim(),
      slug_c: slug,
      tagline_c: form.tagline_c.trim(),
      description_c: form.description_c.trim(),
      features_c: form.features_c
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .join('\n'),
      image_url_c: form.image_url_c.trim(),
      ...(builtIn ? {} : prices),
      show_on_home_c: privatePlan ? false : !!form.show_on_home_c,
      active_c: !!form.active_c,
      sort_c: Number(form.sort_c || 0),
    });
  }

  const busy = saving || checking;

  return (
    <form onSubmit={submit}>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="The product" icon="Package" bodyClassName="space-y-4 py-4">
          {builtIn && (
            <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              {privatePlan
                ? `This row controls the private ${PLANS[form.plan_key_c]?.name ?? form.plan_key_c} plan. Its prices and checkout options stay as they are, and it never shows on the front page, in product listings or in search.`
                : `This row controls the built-in ${PLANS[form.plan_key_c]?.name ?? form.plan_key_c} plan. Its prices and checkout options stay as they are on the site.`}
            </p>
          )}
          <Field label="Name" htmlFor="pr-name">
            <Input id="pr-name" value={form.Name} onChange={changeName} placeholder="Poltergeist Sports Pass" />
          </Field>
          {privatePlan ? (
            <Field label="Private page" hint="Invitation only. Share it directly; it is not listed anywhere on the site.">
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm">{productPath(form)}</p>
            </Field>
          ) : (
            <Field
              label="Page address"
              htmlFor="pr-slug"
              hint="Lowercase letters, numbers and dashes. The product page lives at /p/ followed by this."
            >
              <div className="flex items-center gap-2">
                <span className="shrink-0 font-mono text-sm text-muted-foreground">/p/</span>
                <Input
                  id="pr-slug"
                  value={form.slug_c}
                  onChange={changeSlug}
                  placeholder="poltergeist-sports"
                  className="font-mono"
                />
              </div>
            </Field>
          )}
          <Field label="Tagline" htmlFor="pr-tagline">
            <Input
              id="pr-tagline"
              value={form.tagline_c}
              onChange={text('tagline_c')}
              placeholder="Every match night, no rattling chains"
            />
          </Field>
          <Field label="Description" htmlFor="pr-desc">
            <Textarea
              id="pr-desc"
              rows={5}
              value={form.description_c}
              onChange={text('description_c')}
              placeholder="What it is, who it suits and what comes with it…"
            />
          </Field>
          <Field label="Features" htmlFor="pr-features" hint="One per line. Shown as bullets on the product page.">
            <Textarea
              id="pr-features"
              rows={5}
              value={form.features_c}
              onChange={text('features_c')}
              placeholder={'Live sports in HD\nWorks on every device\nSetup help included'}
            />
          </Field>
        </Panel>

        <div className="space-y-6">
          <Panel title="Photo" icon="Image" bodyClassName="space-y-4 py-4">
            <Field label="Image address" htmlFor="pr-image" hint="Paste a link to a JPG, PNG or WebP. Wide images look best.">
              <Input
                id="pr-image"
                value={form.image_url_c}
                onChange={text('image_url_c')}
                placeholder="https://example.com/product.jpg"
              />
            </Field>
            <div className="flex aspect-video items-center justify-center overflow-hidden rounded-md border border-dashed border-border bg-muted/40">
              {form.image_url_c.trim() ? (
                <img src={form.image_url_c.trim()} alt={form.Name || 'Product photo'} className="h-full w-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-1 text-xs text-muted-foreground">
                  <ApperIcon name="ImageOff" size={20} />
                  No photo yet
                </div>
              )}
            </div>
          </Panel>

          {!builtIn && (
            <Panel title="Prices" icon="Tag" bodyClassName="space-y-3 py-4">
              <p className="text-xs text-muted-foreground">
                In US dollars. Leave a term empty to not sell it. Renewals charge the same term price. One-time is paid
                once and never renews.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {PRODUCT_TERMS.map((t) => (
                  <Field key={t.id} label={t.label} htmlFor={`pr-${t.id}`}>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        $
                      </span>
                      <Input
                        id={`pr-${t.id}`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={form[t.field]}
                        onChange={text(t.field)}
                        placeholder="Not sold"
                        className="pl-7"
                      />
                    </div>
                  </Field>
                ))}
              </div>
            </Panel>
          )}

          <Panel title="Where it shows" icon="Eye" bodyClassName="space-y-3 py-4">
            {privatePlan ? (
              <p className="flex gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                <ApperIcon name="Lock" size={14} className="mt-0.5 shrink-0 text-primary" />
                Always hidden from the public site. Only people with the private link can see it.
              </p>
            ) : (
              <SwitchRow
                id="pr-home"
                label="Show on the front page"
                hint="Off keeps it off the home page. The direct link still works."
                checked={form.show_on_home_c}
                onChange={set('show_on_home_c')}
              />
            )}
            <SwitchRow
              id="pr-active"
              label="On sale"
              hint="Off stops new orders. Existing lines keep running."
              checked={form.active_c}
              onChange={set('active_c')}
            />
            <Field label="Order on the page" htmlFor="pr-sort" hint="Lower numbers show first.">
              <Input
                id="pr-sort"
                type="number"
                step="1"
                value={form.sort_c}
                onChange={text('sort_c')}
                placeholder="10"
                className="w-28"
              />
            </Field>
          </Panel>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 mt-6 flex justify-end gap-2 border-t border-border bg-background/95 py-3 backdrop-blur">
        <Button type="button" variant="outline" asChild>
          <Link to={cancelTo}>Cancel</Link>
        </Button>
        <Button type="submit" disabled={busy}>
          <Busy busy={busy} label={submitLabel} busyLabel={checking ? 'Checking…' : busyLabel} />
        </Button>
      </div>
    </form>
  );
}
